import type {
  DeviceDTO,
  EventDTO,
  LogDTO,
  WsMessage,
} from "../api/contract";

export interface WsHandlers {
  onConnect?: () => void;
  onDisconnect?: () => void;
  onLog?: (log: LogDTO) => void;
  onDeviceState?: (device: DeviceDTO) => void;
  onDeviceRevoked?: (deviceId: string) => void;
  onEvent?: (event: EventDTO) => void;
  onMetricTick?: (msg: { ts: number; reqsPerMin: number }) => void;
}

interface WsManagerState {
  socket: WebSocket | null;
  subscribers: Set<WsHandlers>;
  connected: boolean;
  retry: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  closing: boolean;
}

const state: WsManagerState = {
  socket: null,
  subscribers: new Set(),
  connected: false,
  retry: 0,
  retryTimer: null,
  closing: false,
};

const MAX_BACKOFF_MS = 10_000;
const BASE_BACKOFF_MS = 500;

function wsUrl(): string {
  if (typeof location === "undefined") return "ws://localhost/ws";
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

function clearRetry(): void {
  if (state.retryTimer !== null) {
    clearTimeout(state.retryTimer);
    state.retryTimer = null;
  }
}

function scheduleReconnect(): void {
  if (state.subscribers.size === 0) return;
  if (state.retryTimer !== null) return;
  const delay = Math.min(MAX_BACKOFF_MS, BASE_BACKOFF_MS * Math.pow(2, state.retry));
  state.retry++;
  state.retryTimer = setTimeout(() => {
    state.retryTimer = null;
    connect();
  }, delay);
}

function dispatch(msg: WsMessage): void {
  for (const handlers of state.subscribers) {
    try {
      switch (msg.type) {
        case "hello":
          break;
        case "log":
          handlers.onLog?.(msg.log);
          break;
        case "device_state":
          handlers.onDeviceState?.(msg.device);
          break;
        case "device_revoked":
          handlers.onDeviceRevoked?.(msg.deviceId);
          break;
        case "event":
          handlers.onEvent?.(msg.event);
          break;
        case "metric_tick":
          handlers.onMetricTick?.({ ts: msg.ts, reqsPerMin: msg.reqsPerMin });
          break;
      }
    } catch (err) {
      console.error("ws subscriber threw", err);
    }
  }
}

function notifyConnect(): void {
  for (const h of state.subscribers) {
    try {
      h.onConnect?.();
    } catch (err) {
      console.error("ws onConnect threw", err);
    }
  }
}

function notifyDisconnect(): void {
  for (const h of state.subscribers) {
    try {
      h.onDisconnect?.();
    } catch (err) {
      console.error("ws onDisconnect threw", err);
    }
  }
}

function connect(): void {
  if (state.socket && (state.socket.readyState === WebSocket.OPEN || state.socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  if (state.subscribers.size === 0) return;

  state.closing = false;
  let socket: WebSocket;
  try {
    socket = new WebSocket(wsUrl());
  } catch (err) {
    console.error("ws connect threw", err);
    scheduleReconnect();
    return;
  }
  state.socket = socket;

  socket.onopen = () => {
    if (state.socket !== socket) return;
    clearRetry();
    state.connected = true;
    state.retry = 0;
    notifyConnect();
  };

  socket.onmessage = (ev: MessageEvent) => {
    if (state.socket !== socket) return;
    let msg: WsMessage;
    try {
      msg = JSON.parse(typeof ev.data === "string" ? ev.data : "") as WsMessage;
    } catch (err) {
      console.error("ws bad message", err);
      return;
    }
    dispatch(msg);
  };

  socket.onerror = () => {
    // onclose will fire and handle reconnect
  };

  socket.onclose = () => {
    if (state.socket !== socket) return;
    const wasConnected = state.connected;
    state.connected = false;
    state.socket = null;
    if (wasConnected) notifyDisconnect();
    if (!state.closing) scheduleReconnect();
  };
}

function teardown(): void {
  state.closing = true;
  clearRetry();
  state.retry = 0;
  const socket = state.socket;
  state.socket = null;
  state.connected = false;
  if (socket) {
    try {
      socket.close();
    } catch {
      /* ignore */
    }
  }
}

export function subscribeWs(handlers: WsHandlers): () => void {
  state.subscribers.add(handlers);
  if (state.connected) {
    try {
      handlers.onConnect?.();
    } catch (err) {
      console.error("ws onConnect threw", err);
    }
  } else {
    connect();
  }
  return () => {
    state.subscribers.delete(handlers);
    if (state.subscribers.size === 0) teardown();
  };
}

export function isWsConnected(): boolean {
  return state.connected;
}
